import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Hammer, Heart, Globe, Users, Mail, MapPin, Phone, Facebook, Twitter, Instagram, Linkedin, Building, AlertCircle, MessageSquare } from 'lucide-react';
import Button from '../components/ui/Button';
import companyInfo from '../data/companyInfo.json';
import { CREATE_CONTACT_MESSAGE } from '../graphql/mutations/createContactMessage';
import { GET_STATISTICS } from '../graphql/queries/statistics';

const About: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [createContactMessage, { loading: isLoading }] = useMutation(CREATE_CONTACT_MESSAGE);
  const { data: statsData } = useQuery(GET_STATISTICS);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate email
    if (!validateEmail(formData.email)) {
      setError('Indtast venligst en gyldig email-adresse.');
      return;
    }

    try {
      const { data } = await createContactMessage({
        variables: {
          input: {
            name: formData.name,
            email: formData.email,
            subject: 'Kontakt os',
            message: formData.message,
          },
        },
      });

      if (data?.createContactMessage?.contactMessage) {
        setSuccess(true);
        setFormData({ name: '', email: '', message: '' });
      } else if (data?.createContactMessage?.errors) {
        setError(data.createContactMessage.errors);
      } else {
        setError('Kunne ikke sende besked. Pr\u00f8v venligst igen.');
      }
    } catch (err) {
      setError('Kunne ikke sende besked. Pr\u00f8v venligst igen.');
    }
  };

  const stats = [
    { label: 'Aktive brugere', value: statsData?.statistics?.activeUsersCount?.toLocaleString('da-DK') || '0' },
    { label: 'Værktøj oprettet', value: statsData?.statistics?.toolsCount?.toLocaleString('da-DK') || '0' },
    { label: 'Udlån gennemført', value: statsData?.statistics?.completedLendsCount?.toLocaleString('da-DK') || '0' },
  ];

  const values = [
    {
      icon: <Heart className="h-6 w-6" />,
      title: 'Fællesskaber Først',
      description: 'Vi tror på kraften i deling og opbygning af stærkere fællesskaber gennem samarbejde.',
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: 'Bæredygtighed',
      description: 'Ved at dele ressourcer reducerer vi affald og miljøpåvirkning samtidig med, at vi fremmer ansvarligt forbrug.',
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Tillid & sikkerhed',
      description: 'Vi prioriterer at skabe et sikkert og tillidsfuldt miljø for alle vores fællesskabsmedlemmer.',
    },
    {
      icon: <Hammer className="h-6 w-6" />,
      title: 'Tilgængelighed',
      description: 'Vi gør værktøj og ressourcer tilgængelige for alle, uanset deres økonomiske situation.',
    },
  ];

  const team = [
    {
      name: 'Anders .....',
      role: 'CEO & Co-founder',
      image: '/images/us/ceo.png',
      bio: 'Serieiværksætter med planlæggergen og naturligt overblik.',
    },
    {
      name: 'Jeppe .....',
      role: 'CTO & Co-founder',
      image: '/images/us/cto.png',
      bio: 'Tech-veteran fokuseret på at bygge platforme der forbinder mennesker.',
    },
    {
      name: 'Thomas .....',
      role: 'COO & Co-founder',
      image: '/images/us/coo.png',
      bio: 'Operationel ekspert med erfaring i daglig drift og procesoptimering.',
    },
    {
      name: 'Simon .....',
      role: 'CFO & Co-founder',
      image: '/images/us/cfo.png',
      bio: 'Finansiel specialist med fokus på bæredygtig vækst og økonomisk styring.',
    },
    {
      name: 'Adam .....',
      role: 'CPO & Co-founder',
      image: '/images/us/cpo.png',
      bio: 'Produktekspert med passion for brugeroplevelser og innovation.',
    },
    {
      name: 'Thure .....',
      role: 'CSO & Co-founder',
      image: '/images/us/cso.png',
      bio: 'Strategisk tænker med fokus på langsigtet vækst og markedsudvikling.',
    },
    {
      name: 'Johnny .....',
      role: 'CCO & CMO & Co-founder',
      image: '/images/us/cmo.png',
      bio: 'Kommerciel- og marketingekspert med fokus på vækst, omsætning og brugerengagement.',
    },
    {
      name: 'Jannik .....',
      role: 'CIO & Co-founder',
      image: '/images/us/cio.png',
      bio: 'Innovationsdriver med passion for nye løsninger og teknologiske fremskridt.',
    },
  ];

  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-green-600 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            // src="https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            src="/images/programmer.png?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
            alt="Team collaboration"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              Opbygger fællesskaber gennem deling
            </h1>
            <p className="text-xl text-green-100">
              DoMore er mere end bare en platform til deling af værktøj. Vi er et fællesskabsdrevet initiativ, der arbejder for at skabe en mere bæredygtig og forbundet verden.
            </p>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Vores mission</h2>
            <p className="text-xl text-gray-600">
              Vi har en mission om at ændre måden, fællesskaber deler ressourcer på. Ved at forbinde mennesker, der har brug for værktøj, med dem der har det, skaber vi en mere bæredygtig og samarbejdende fremtid, samtidig med at vi hjælper folk med at spare penge og reducere spild.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div key={value.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-green-600 mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="bg-white py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Mød vores team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Vi er et passioneret team af fællesskabsbyggere, teknologer og fortalere for bæredygtighed, 
              der arbejder for at gøre værktøjsdeling tilgængelig for alle.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member) => (
              <div key={member.name} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="aspect-w-3 aspect-h-3">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                  <p className="text-sm text-green-600 mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-16 sm:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Kontakt os</h2>
              <p className="text-xl text-gray-600">
                Har du spørgsmål eller vil du vide mere om DoMore? Vi vil meget gerne høre fra dig.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Kontaktinformation</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Building className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-600">{companyInfo.name}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-600">{`${companyInfo.address.street}, ${companyInfo.address.postalCode} ${companyInfo.address.city}, ${companyInfo.address.country}`}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-green-600 mr-3" />
                    <a href={`mailto:${companyInfo.contact.email.general}`} className="text-gray-600 hover:text-green-600">
                      {companyInfo.contact.email.general}
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-green-600 mr-3" />
                    <span className="text-gray-600">{companyInfo.contact.phone}</span>
                  </div>
                </div>

                <div className="mt-8">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Følg os</h4>
                  <div className="flex space-x-4">
                    <a href="#" className="text-gray-400 hover:text-gray-500">
                      <Facebook className="h-5 w-5" />
                    </a>
                    <a href="#" className="text-gray-400 hover:text-gray-500">
                      <Twitter className="h-5 w-5" />
                    </a>
                    <a href="#" className="text-gray-400 hover:text-gray-500">
                      <Instagram className="h-5 w-5" />
                    </a>
                    <a href="#" className="text-gray-400 hover:text-gray-500">
                      <Linkedin className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Send os en besked</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Navn
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Besked
                    </label>
                    <textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                      required
                    />
                  </div>

                  {error && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-red-800">{error}</h3>
                        </div>
                      </div>
                    </div>
                  )}

                  {success && (
                    <div className="rounded-md bg-green-50 p-4">
                      <div className="flex">
                        <MessageSquare className="h-5 w-5 text-green-400" />
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">
                            Besked sendt! Vi vender tilbage hurtigst muligt.
                          </h3>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button type="submit" variant="primary" fullWidth isLoading={isLoading}>
                    Send Besked
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-green-600 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Bliv en del af vores fællesskab</h2>
            <p className="text-xl text-green-100 mb-8">
              Bliv en del af delingsrevolutionen. Begynd at låne eller udlåne værktøj i dag.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup">
                <button
                  className="inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white px-6 py-3 text-base border-2 border-transparent bg-white text-green-600 hover:bg-green-50"
                >
                  Kom i Gang
                </button>
              </Link>
              <Link to="/browse">
                <button
                  className="inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white px-6 py-3 text-base border-2 border-white text-white hover:bg-white hover:text-green-600"
                >
                  Gennemse Værktøj
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;